import { QueryFunctionContext } from 'react-query'
import request from 'services/axios'
import {
    demoCashboxDetail,
    demoCashboxMembers,
    demoCashboxTransactions,
    demoCashboxWinners,
    isDemoCashbox,
} from './demo'

/**
 * صندوق قرعه‌کشی — the v1 `cashbox_management` API.
 *
 * Paths are copied from the Android client's `Constants.API_CASHBOX_*` so the
 * two clients provably hit the same routes. Two of those constants are
 * misleading and are NOT reproduced here:
 *
 *   - `search-moneypool/{slug}/` — the backend route is `search-cashbox`;
 *     Android's constant is a stale copy-paste and that call 404s today.
 *   - `request-drawable-memberships/` — superseded by `request-drawables/`,
 *     which also returns share groups. Only the latter is used.
 */

const box = (id: string | number) => `/cashbox_management/cashbox/${id}`
const scoped = (id: string | number) => `/cashbox_management/${id}`

/* ---------------------------------------------------------------- create */

export interface CreateCashboxPayload {
    name: string
    image_id: number
    /** owner's own whole shares; 0 is allowed (an accountant-only manager) */
    number_of_shares: number
    /**
     * `cms` = کارمزدی (fee taken from members on each online payment),
     * `sub` = اشتراکی (the manager pays for the box itself).
     *
     * This matters more than it looks: `create_cashbox` puts a SUBSCRIPTION
     * box straight into state `unp` — unusable until an Order is paid — while
     * a COMMISSION box is born `nct` and can be activated immediately. Android
     * asks for it at creation (`Constants.FIELD_BOX_PLAN`); omitting it made
     * the PWA create boxes nobody could open.
     */
    plan: 'cms' | 'sub'
    /** backend `choice.BOX_GOAL` code; see components/fundGoal */
    goal: string
    goal_description: string
    period: {
        share_value: number
        interval: CashboxInterval
        day_of_draw: number
        number_of_loans: number
        is_manager_first_winner: boolean
    }
}

/**
 * `create_cashbox` pops `period` off the body before validating, so `period`
 * is mandatory — posting a flat body 500s on the KeyError. `trust_state` is
 * always `trst` here: the multiplexed (worried) variant needs a verified
 * PayPing bank account, which the PWA has no flow for.
 */
export const createCashbox = async (payload: CreateCashboxPayload) => {
    const { data }: { data: { message: string; cashbox: ICashboxDetail } } =
        await request.post('/cashbox_management/cashbox/create/', {
            ...payload,
            trust_state: 'trst',
        })
    return data
}

export interface UpdateCashboxPayload {
    id: string | number
    name: string
    image_id: number
    type?: string
    notification_announce?: string
    sms_announce?: string
    manual_cashout?: boolean
    editable_members?: boolean
    /** owner's own share count; applied to the owner membership */
    number_of_shares?: number
    period: {
        share_value: number
        interval: CashboxInterval
        day_of_draw: number
        number_of_loans: number
        is_manager_first_winner: boolean
    }
}

/** `update_cashbox` rejects a body without `period` (400 "period not in data"). */
export const updateCashbox = async ({ id, ...body }: UpdateCashboxPayload) => {
    const { data } = await request.post(`${box(id)}/update/`, body)
    return data
}

/* ------------------------------------------------------------------ read */

/*
 * The four read calls below answer from a local fixture for the `demo` box.
 *
 * That box is what the قرعه‌کشی landing (`/q/<code>`) renders: a visitor with
 * no account gets the real cashbox screen — real components, real formatting,
 * a working search box — without a session and without a request. Answering
 * here rather than in the hooks keeps every caller (and react-query's cache
 * keys, including the per-search ones) working unchanged.
 *
 * `isDemoCashbox` matches a non-numeric sentinel, so no real box id can reach
 * these branches. Nothing writable is stubbed: a demo visitor who taps an
 * action is sent to the CTA, never to a mutation.
 */

export const getCashbox = async (ctx: QueryFunctionContext) => {
    const id = ctx.queryKey[1] as string
    if (isDemoCashbox(id)) return demoCashboxDetail()

    const { data }: { data: ICashboxDetail } = await request.get(`${box(id)}/`)
    return data
}

export const getCashboxMembers = async (
    id: string | number,
    { q, page }: { q?: string; page?: number } = {}
) => {
    if (isDemoCashbox(id)) return demoCashboxMembers(q)

    const params = new URLSearchParams()
    if (q) params.set('q', q)
    if (page) params.set('page', String(page))
    const query = params.toString()
    const { data }: { data: ICashboxMembersResponse } = await request.get(
        `${box(id)}/list-memberships-and-share-groups/${
            query ? `?${query}` : ''
        }`
    )
    return data
}

export const getCashboxTransactions = async (
    id: string | number,
    { q, date }: { q?: string; date?: string } = {}
) => {
    if (isDemoCashbox(id)) return demoCashboxTransactions(q)

    const params = new URLSearchParams()
    if (q) params.set('q', q)
    if (date) params.set('date', date)
    const query = params.toString()
    const { data }: { data: { results: Array<ICashboxTransaction> } } =
        await request.get(
            `${box(id)}/transaction-list/${query ? `?${query}` : ''}`
        )
    return data
}

export const getCashboxWinners = async (
    id: string | number,
    { q, date }: { q?: string; date?: string } = {}
) => {
    if (isDemoCashbox(id)) return demoCashboxWinners(q)

    const params = new URLSearchParams()
    if (q) params.set('q', q)
    if (date) params.set('date', date)
    const query = params.toString()
    const { data }: { data: { results: Array<ICashboxWinner> } } =
        await request.get(`${box(id)}/winners-list/${query ? `?${query}` : ''}`)
    return data
}

export const getCashboxCycles = async (ctx: QueryFunctionContext) => {
    const { data }: { data: { results: Array<ICashboxCycle> } } =
        await request.get(`${box(ctx.queryKey[1] as string)}/cycles-list/`)
    return data
}

/** Owner-only. Every drawable (membership + share group) with its odds. */
export const getCashboxDrawables = async (id: string | number, q?: string) => {
    const { data }: { data: { results: Array<ICashboxDrawable> } } =
        await request.get(
            `${box(id)}/request-drawables/${
                q ? `?q=${encodeURIComponent(q)}` : ''
            }`
        )
    return data
}

export const getCashboxInviteList = async () => {
    const { data }: { data: { results: Array<ICashboxDetail> } } =
        await request.get(
            '/cashbox_management/cashbox/invited-and-request-list/'
        )
    return data
}

export const getCashboxBulletins = async (ctx: QueryFunctionContext) => {
    const { data }: { data: Array<ICashboxBulletin> } = await request.get(
        `/cashbox_management/bulletins/${ctx.queryKey[1]}/`
    )
    return data
}

/* ----------------------------------------------------------- membership */

export const inviteToCashbox = async (payload: {
    id: string | number
    phone_number: string
    first_name?: string
    last_name?: string
    number_of_shares: number
    role?: CashboxRole
}) => {
    const { data } = await request.post(
        `${scoped(payload.id)}/membership/invite/`,
        {
            phone_number: payload.phone_number,
            first_name: payload.first_name ?? '',
            last_name: payload.last_name ?? '',
            membership: {
                number_of_shares: payload.number_of_shares,
                role: payload.role ?? 'nrm',
            },
        }
    )
    return data
}

export const updateMembershipShares = async (payload: {
    id: string | number
    membershipId: number
    number_of_shares: number
}) => {
    const { data } = await request.post(
        `${scoped(payload.id)}/membership/update/${payload.membershipId}/`,
        { number_of_shares: payload.number_of_shares }
    )
    return data
}

export const kickOutMember = async (payload: {
    id: string | number
    membershipId: number
}) => {
    const { data } = await request.post(
        `${scoped(payload.id)}/membership/kick-out-member/${
            payload.membershipId
        }/`
    )
    return data
}

export const leaveCashbox = async (id: string | number) => {
    const { data } = await request.post(`${scoped(id)}/membership/leave/`)
    return data
}

export const acceptCashboxInvitation = async (id: string | number) => {
    const { data } = await request.post(
        `${scoped(id)}/membership/accept-invitation/`
    )
    return data
}

export const denyCashboxInvitation = async (id: string | number) => {
    const { data } = await request.post(
        `${scoped(id)}/membership/deny-invitation/`
    )
    return data
}

export const acceptCashboxRequest = async (payload: {
    id: string | number
    membershipId: number
}) => {
    const { data } = await request.post(
        `${scoped(payload.id)}/membership/accept-request/${
            payload.membershipId
        }/`
    )
    return data
}

export const denyCashboxRequest = async (payload: {
    id: string | number
    membershipId: number
}) => {
    const { data } = await request.post(
        `${scoped(payload.id)}/membership/deny-request/${payload.membershipId}/`
    )
    return data
}

export const getCashboxGroupPay = async (id: string | number, q?: string) => {
    const { data }: { data: ICashboxGroupPayResponse } = await request.get(
        `${scoped(id)}/membership/init-group-pay/${
            q ? `?q=${encodeURIComponent(q)}` : ''
        }`
    )
    return data
}

/* --------------------------------------------------------- share groups */

export const getCashboxShareGroups = async (ctx: QueryFunctionContext) => {
    const { data }: { data: { results: Array<ICashboxShareGroup> } } =
        await request.get(
            `${scoped(ctx.queryKey[1] as string)}/share_group/list/`
        )
    return data
}

export interface ShareGroupMemberInput {
    phone_number: string
    share_residue: number
}

export const createCashboxShareGroup = async (payload: {
    id: string | number
    name: string
    group_members: Array<ShareGroupMemberInput>
    shareGroupId?: number
}) => {
    const { data } = await request.post(
        `${scoped(payload.id)}/share_group/create/`,
        {
            share_group: {
                id: payload.shareGroupId ?? 0,
                name: payload.name,
            },
            group_members: payload.group_members,
        }
    )
    return data
}

export const updateCashboxShareGroup = async (payload: {
    id: string | number
    shareGroupId: number
    name: string
    group_members: Array<ShareGroupMemberInput>
}) => {
    const { data } = await request.post(
        `${scoped(payload.id)}/share_group/update/${payload.shareGroupId}/`,
        {
            share_group: { id: payload.shareGroupId, name: payload.name },
            group_members: payload.group_members,
        }
    )
    return data
}

export const deleteCashboxShareGroup = async (payload: {
    id: string | number
    shareGroupId: number
}) => {
    const { data } = await request.post(
        `${scoped(payload.id)}/share_group/kick-out/${payload.shareGroupId}/`
    )
    return data
}

/* --------------------------------------------------------- box lifecycle */

export const activateCashbox = async (id: string | number) => {
    const { data } = await request.post(`${box(id)}/activate/`)
    return data
}

export const deactivateCashbox = async (id: string | number) => {
    const { data } = await request.post(`${box(id)}/deactivate/`)
    return data
}

export const removeCashbox = async (id: string | number) => {
    const { data } = await request.post(`${box(id)}/remove/`)
    return data
}

export const initiateCashboxPeriod = async (id: string | number) => {
    const { data } = await request.post(`${box(id)}/initiate-period/`)
    return data
}

/** jalaliDate must be `YYYY-MM-DD` in Jalali. */
export const changeCashboxDrawDate = async (payload: {
    id: string | number
    jalaliDate: string
}) => {
    const { data } = await request.post(
        `${box(payload.id)}/change-cycle-draw-date/`,
        { new_draw_date: payload.jalaliDate }
    )
    return data
}

/* ------------------------------------------------------------------ draw */

export interface DrawPayload {
    id: string | number
    /** `ato` picks at random, `mnl` uses the ids the owner chose */
    type: 'ato' | 'mnl'
    winners_count?: number
    /** manual: the exact winners */
    winners?: Array<{ id: number; winner_type: 'membership' | 'share_group' }>
    /** automatic: restrict the pool the random pick draws from */
    selected_drawables?: Array<{
        id: number
        type: 'membership' | 'share_group'
    }>
}

export const drawCashbox = async ({ id, ...body }: DrawPayload) => {
    const { data } = await request.post(`${box(id)}/draw/`, body)
    return data
}

/* --------------------------------------------------------------- payouts */

interface CashboxBankAccount {
    id: number
    bank_name: string
    owner_name: string
    sheba_number?: string
    card_number?: string | number | null
    member?: number
}

/** Owner-only. Memberships still eligible for a payout + the box's accounts. */
export const initManualCashout = async (id: string | number) => {
    const {
        data,
    }: {
        data: {
            memberships: Array<ICashboxMembership>
            bank_accounts: Array<CashboxBankAccount>
        }
    } = await request.get(`${box(id)}/manual-cashout-init/`)
    return data
}

/**
 * Owner pays a member out of the box balance, and the backend records a Winner
 * for them (`create_winner` inside the view) — so this both moves the money and
 * burns one of that member's shares. It is refused unless `manual_cashout` is
 * on for the box.
 */
export const createManualCashout = async (payload: {
    id: string | number
    receiver_id: number
    bank_account_id: number
    amount: number
    new_cycle: boolean
}) => {
    const { id, ...body } = payload
    const { data } = await request.post(
        `/payment/${id}/create-manual-cashout/`,
        body
    )
    return data
}

/**
 * The winner claiming their own prize.
 *
 * `wlt` credits the Hamyan wallet immediately; `mba` starts a transfer to the
 * member's bank account and needs `bank_account_id`.
 */
export const claimCashboxPrize = async (payload: {
    id: string | number
    destination: 'wlt' | 'mba'
    bank_account_id?: number
}) => {
    const { id, ...body } = payload
    const { data } = await request.post(
        `/payment/${id}/claim-cashbox-prize/`,
        body
    )
    return data
}

/**
 * Owner depositing the loan to the winners of the last cycle.
 *
 * Mirrors Android `payCashboxPrizes`: one transaction per winner, out of the
 * box balance (`hbb`, or `bba` when the box is worried) into the winner's bank
 * account. Modern clients draw with `create_cashouts=False`, so the draw
 * leaves winners unpaid and this is what settles them.
 */
export const payCashboxWinners = async (payload: {
    items: Array<{
        cycle_id: number
        amount: number
        receiver_id: number
        bank_account_id: number
    }>
    isWorried: boolean
    gatewayId?: number
}) => {
    const { data } = await request.post(
        '/api/v1/payment/transactions/',
        payload.items.map((item) => ({
            ctx_type: 'ccl',
            ctx_id: item.cycle_id,
            source: payload.isWorried ? 'bba' : 'hbb',
            destination: 'mba',
            amount: item.amount,
            receiver: item.receiver_id,
            bank_account: item.bank_account_id,
            ...(payload.gatewayId ? { gateway: payload.gatewayId } : {}),
        }))
    )
    return data
}

/* ------------------------------------------------------------- bulletins */

/** Who a new post reaches: every valid member but its author (round 2). */
export const getCashboxBulletinReach = async (
    id: string | number
): Promise<{ total: number; messenger: number; app_only: number }> => {
    const {
        data,
    }: { data: { total: number; messenger: number; app_only: number } } =
        await request.get(`/cashbox_management/bulletins/${id}/reach`)
    return data
}

export const createCashboxBulletin = async (payload: {
    id: string | number
    text: string
    sticker: number
}) => {
    const { data }: { data: ICashboxBulletin } = await request.post(
        `/cashbox_management/bulletins/${payload.id}/create`,
        { text: payload.text, sticker: payload.sticker }
    )
    return data
}

export const updateCashboxBulletin = async (payload: {
    id: string | number
    bulletinId: number
    text: string
    sticker: number
}) => {
    const { data }: { data: ICashboxBulletin } = await request.put(
        `/cashbox_management/bulletins/${payload.id}/${payload.bulletinId}/update`,
        { text: payload.text, sticker: payload.sticker }
    )
    return data
}

export const deleteCashboxBulletin = async (payload: {
    id: string | number
    bulletinId: number
}) => {
    const { data } = await request.delete(
        `/cashbox_management/bulletins/${payload.id}/${payload.bulletinId}/delete`
    )
    return data
}
